/**
 * Cómo se pinta y cómo se llama cada estado de la migración.
 *
 * Va aparte porque lo comparten tres sitios que no se ven entre sí: el detalle de una hoja,
 * el resumen del plan y la tarjeta de la cotización. Cuando cada uno traía su propio mapa,
 * "error" era rojo en una pantalla y gris en otra, y el asesor tenía que aprenderse dos
 * lenguajes para el mismo dato.
 */

/** Estado de una FILA dentro de un archivo. */
export const ESTADO_FILA = {
  crear: { etiqueta: 'Nuevo', variante: 'success' },
  actualizar: { etiqueta: 'Actualiza', variante: 'default' },
  aviso: { etiqueta: 'Revisar', variante: 'warning' },
  error: { etiqueta: 'No entra', variante: 'danger' },
}

/** Estado de una HOJA dentro del proyecto de migración. */
export const ESTADO_HOJA = {
  pendiente: { etiqueta: 'Sin asignar', variante: 'secondary' },
  mapeada: { etiqueta: 'Lista', variante: 'default' },
  ensayada: { etiqueta: 'Ensayada', variante: 'default' },
  cargada: { etiqueta: 'Cargada', variante: 'success' },
  omitida: { etiqueta: 'No se carga', variante: 'secondary' },
  error: { etiqueta: 'Con error', variante: 'danger' },
}

/** Estado del PROYECTO entero. El orden es el del proceso, y se usa para el stepper. */
export const ESTADO_PROYECTO = {
  recopilando: { etiqueta: 'Recopilando', variante: 'secondary' },
  preparando: { etiqueta: 'Preparando', variante: 'default' },
  revisado: { etiqueta: 'Revisado', variante: 'default' },
  cuadrado: { etiqueta: 'Cuadrado', variante: 'default' },
  cargando: { etiqueta: 'Cargando', variante: 'warning' },
  entregado: { etiqueta: 'Entregado', variante: 'success' },
  descartado: { etiqueta: 'Descartado', variante: 'secondary' },
}

export const PASOS_PROYECTO = [
  { clave: 'recopilando', titulo: 'Recopilar', descripcion: 'Suelta aquí todo lo que te dio' },
  { clave: 'preparando', titulo: 'Preparar', descripcion: 'Qué es cada hoja y cómo se mapea' },
  { clave: 'cuadrado', titulo: 'Cuadrar', descripcion: 'Que las hojas encajen entre sí' },
  { clave: 'entregado', titulo: 'Cargar', descripcion: 'Escribir en la empresa' },
]

/**
 * Qué impide ESCRIBIR en la empresa del cliente. Fuente única de verdad.
 *
 * Antes esto no existía: el aviso de "falta asignar" vivía en el detalle de la hoja, el
 * candado de "bloqueada por" en el plan, y los bloqueantes del cuadre en su propia tarjeta —
 * los tres como TEXTO. El botón que escribe estaba siempre habilitado, así que se podía
 * cargar en la empresa de un cliente real un proyecto a medias y descubrirlo después.
 *
 * Navegar entre pasos sigue siendo libre: mirar no hace daño. Lo que se cierra es la puerta.
 *
 * @returns {Array<{clave:string, motivo:string, hojaId?:string}>} vacío = se puede cargar.
 */
/** La tarjeta de una entidad: las hojas de líneas van con su cabecera (mismo criterio que el api). */
export function tarjetaDe(entidad, especificaciones) {
  return especificaciones?.[entidad]?.hijaDe?.padre || entidad
}

/** Con `entidad`, solo lo que impide cargar ESA tarjeta; sin ella, lo que impide cargar todo. */
export function bloqueosDeCarga(proyecto, especificaciones, entidad) {
  if (!proyecto) return [{ clave: 'sin-proyecto', motivo: 'Todavía no has traído ningún archivo.' }]
  const hojas = proyecto.hojas || []
  const bloqueos = []

  const utiles = hojas.filter(
    (h) =>
      h.estado !== 'omitida' &&
      h.estado !== 'cargada' &&
      (!entidad || (h.entidad && tarjetaDe(h.entidad, especificaciones) === entidad))
  )

  if (hojas.length === 0) {
    bloqueos.push({ clave: 'sin-hojas', motivo: 'Todavía no has traído ningún archivo.' })
  }

  for (const h of utiles) {
    if (!h.entidad) {
      bloqueos.push({
        clave: `sin-entidad:${h.hojaId}`,
        hojaId: h.hojaId,
        motivo: `«${h.nombreHoja}» no tiene sección asignada: nadie sabe qué son esas filas.`,
      })
      continue
    }
    // Los campos requeridos SIN mapear: ninguna fila de esa hoja entraría.
    const spec = especificaciones?.[h.entidad]
    const usados = new Set(Object.values(h.mapeo || {}))
    const faltan = (spec?.campos || [])
      .filter((c) => c.requerido && !c.derivado && !usados.has(c.clave))
      .map((c) => c.etiqueta)
    if (faltan.length > 0) {
      bloqueos.push({
        clave: `requeridos:${h.hojaId}`,
        hojaId: h.hojaId,
        motivo: `«${h.nombreHoja}» no tiene asignado ${faltan.join(', ')}. Sin eso no entra ninguna fila.`,
      })
    }
  }

  const bloqueantes = proyecto.cuadre?.bloqueantes || 0
  if (bloqueantes > 0) {
    bloqueos.push({
      clave: 'cuadre',
      motivo: `El cuadre encontró ${bloqueantes} problema(s) que impiden operar. Revísalos en el paso Cuadrar.`,
    })
  }

  return bloqueos
}

/** Severidad de un hallazgo de calidad. */
export const SEVERIDAD = {
  bloqueante: { etiqueta: 'Impide operar', variante: 'danger' },
  aviso: { etiqueta: 'Solo ensucia', variante: 'warning' },
}

/**
 * Resumen de una migración en una línea, para la tarjeta de la cotización.
 *
 * Se dice en secciones y filas —no en "hojas" ni "entidades"— porque es lo que el asesor le
 * repite al cliente por teléfono. Y los errores salen SIEMPRE que los haya, aunque sean
 * pocos: son la razón por la que hay que volver a esta pantalla.
 */
/** Etiquetas de los ítems de checklist aún no completos, para el aviso al pausar. */
export function pendientesChecklist(checklist = []) {
  return checklist.filter((c) => c.estado !== 'completo').map((c) => c.etiqueta)
}

export function resumenMigracion(proyecto) {
  if (!proyecto) return null
  const t = proyecto.totales || {}
  const partes = []
  if (t.hojas) {
    const listas = (t.hojasCargadas || 0) + (t.hojasListas || 0)
    partes.push(`${listas} de ${t.hojas} secciones`)
  }
  if (t.filas) partes.push(`${t.filas.toLocaleString('es-SV')} filas`)
  const conErrores = (proyecto.hojas || []).filter((h) => h.carga?.errores > 0 || h.ensayo?.errores > 0).length
  if (conErrores > 0) partes.push(`${conErrores} con errores`)
  if (t.sinReconocer > 0) partes.push(`${t.sinReconocer} sin reconocer`)
  return partes.join(' · ')
}
